/**
 * Client-only capability + preference checks that gate the landing page's
 * heavier motion. All guarded so nothing runs server-side / pre-mount.
 *
 * The design-system's global `prefers-reduced-motion` CSS rule only neutralises
 * CSS animations/transitions — it does NOT stop a requestAnimationFrame loop,
 * Lenis, or WebGL. These helpers are the JS-level guard for those.
 */

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function hasWebGL(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    return (
      !!window.WebGLRenderingContext &&
      !!(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch {
    return false;
  }
}

/**
 * Should we mount the heavy WebGL hero scene at all? No on: reduced-motion,
 * phones / coarse-pointer / small viewports (keep them light + fast), or when
 * WebGL is unavailable. Callers render the static aurora hero instead.
 */
export function shouldRender3D(): boolean {
  if (typeof window === "undefined") return false;
  if (prefersReducedMotion()) return false;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const small = window.matchMedia("(max-width: 820px)").matches;
  if (coarse || small) return false;
  return hasWebGL();
}
